import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormControl } from '@angular/forms';
import { JobListArgs } from 'src/shared/api';
import {
	takeUntil,
	debounceTime,
	distinctUntilChanged,
	delay,
	first,
} from 'rxjs/operators';
import { displayQuoteStatus } from 'src/shared/enums';
import { animateList } from 'src/shared/animations';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { quotesFragments } from '../quotes.routes';
import * as QuoteListActions from './+state/quote-list.actions';
import * as QuoteListSelectors from './+state/quote-list.selectors';
import { Store, select } from '@ngrx/store';
import { customersFragments } from 'src/admin-app/customers/customers.routes';
import { railroadsFragments } from 'src/admin-app/railroads/railroads.routes';
import { FilterCollection } from 'src/admin-app/utilities/list-filtering/shared';
import {
	withAbstractControl,
	withMatPaginator,
	withMatSort,
} from 'src/admin-app/utilities/list-filtering';
import { QuoteListPartialState } from './+state/quote-list.reducer';

@Component({
	templateUrl: './quote-list.component.html',
	styleUrls: ['./quote-list.component.scss'],
	animations: [animateList],
})
export class QuoteListComponent extends SelfDestruct
	implements AfterViewInit, OnDestroy {
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	readonly emptyColumns = ['empty'];
	readonly resultColumns = [
		'number',
		'start',
		'customer',
		'railroad',
		'quoteStatus',
		'modified',
		'description',
		'delete',
	];

	readonly customersFragments = customersFragments;
	readonly railroadsFragments = railroadsFragments;
	readonly quotesFragments = quotesFragments;
	readonly displayQuoteStatus = displayQuoteStatus;

	readonly filter = new FormControl('') as FormControl;

	readonly isLoading$ = this.store.pipe(
		select(QuoteListSelectors.getLoadingStatus)
	);
	readonly listMessage$ = this.store.pipe(
		select(QuoteListSelectors.getListMessage)
	);

	readonly pagedQuotes$ = this.store.pipe(
		select(QuoteListSelectors.getFilteredAndPagedQuotes)
	);

	readonly pagedQuotesCount$ = this.store.pipe(
		select(QuoteListSelectors.getPagedQuotesCount)
	);

	constructor(private readonly store: Store<QuoteListPartialState>) {
		super();
	}

	ngAfterViewInit() {
		const filters = new FilterCollection<JobListArgs>(
			withAbstractControl(this.filter),
			withMatPaginator(this.paginator),
			withMatSort(this.sort)
		);

		filters.valueChanges
			.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe((v) =>
				this.store.dispatch(
					QuoteListActions.filtersUpdated({ filters: v })
				)
			);

		// Initialize filters with URL value
		this.store
			.pipe(
				select(QuoteListSelectors.getListArgs),
				delay(0),
				first<JobListArgs>(Boolean)
			)
			.subscribe((f) => filters.setValue(f));
	}

	cancelQuote(quoteId: number) {
		this.store.dispatch(
			QuoteListActions.userConfirmedCancelQuote({ quoteId: quoteId })
		);
	}

	ngOnDestroy() {
		this.store.dispatch(QuoteListActions.quoteListDestroyed());
		super.ngOnDestroy();
	}
}
